import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle2Icon, XCircleIcon, LockIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import ApiClient from '@/shared/services/api';
import type { PasswordResetResult } from '@/shared/types/api';
import { Wordmark } from '@/shared/components/Wordmark';
import { Spinner } from '@/shared/components/Spinner';
import { ThemeToggle } from '@/shared/components/ThemeToggle';
import { newPasswordSchema, type NewPasswordFormData } from '@/features/auth/lib/passwordSchema';

type Status = 'form' | 'success' | 'invalid';

/** History-entry state that carries the reset token once it is out of the URL. */
interface ResetPasswordState {
  token?: string;
}

// The server explains every failure itself (PasswordResetResult messages); these are only used
// when a response carries no message at all.
const FALLBACK_INVALID_LINK = 'This password reset link is invalid or has expired.';
const FALLBACK_PASSWORD_REJECTED = 'The password does not meet the requirements.';

/**
 * SC-296: landing page for the emailed reset link (/reset-password?token=…). Sets the new
 * password and sends the user back to sign in - the reset signs out every session and never
 * signs in by itself.
 */
export const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  // From the emailed link's query string on first load; after that from the history entry's state
  // (below), which is also where a reload of the page finds it.
  const [token] = useState(
    () => searchParams.get('token') ?? (location.state as ResetPasswordState | null)?.token ?? ''
  );

  // The token is a credential: keep it out of the address bar, where it would be copied, shared or
  // screenshotted, and out of any history entry created from here on. `replace` rewrites only the
  // current entry, moving the token from its URL into its (non-displayed) state so a reload still
  // works. It does not reach entries already recorded elsewhere (e.g. the email client or a
  // browser that synced the URL before this ran).
  useEffect(() => {
    const fromQuery = searchParams.get('token');
    if (fromQuery !== null) {
      navigate(location.pathname, { replace: true, state: { token: fromQuery } satisfies ResetPasswordState });
    }
  }, [searchParams, navigate, location.pathname]);

  const [status, setStatus] = useState<Status>(token ? 'form' : 'invalid');
  const [message, setMessage] = useState<string>(
    token ? '' : 'This password reset link is invalid or incomplete.'
  );
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<NewPasswordFormData>({
    resolver: zodResolver(newPasswordSchema),
  });

  // Password-policy failures come back as a list and keep the form (the link is still valid);
  // a bare message means the link itself is unusable.
  const applyFailure = (result: Partial<PasswordResetResult> | undefined, httpStatus?: number) => {
    if (httpStatus === 429) {
      setMessage('Too many attempts. Please wait a few minutes and try again.');
      return;
    }
    // A server or network failure says nothing about the link: keep the form so the user can
    // retry with the same link instead of being told it is dead.
    if (httpStatus === undefined || httpStatus >= 500) {
      setMessage('Something went wrong while resetting your password. Please try again.');
      return;
    }
    if (result?.errors && result.errors.length > 0) {
      setServerErrors(result.errors);
      setMessage(result.message || FALLBACK_PASSWORD_REJECTED);
    } else {
      setStatus('invalid');
      setMessage(result?.message || FALLBACK_INVALID_LINK);
    }
  };

  const onSubmit = async (data: NewPasswordFormData) => {
    setServerErrors([]);
    setMessage('');
    try {
      const result = await ApiClient.resetPassword({ Token: token, NewPassword: data.password });
      if (result.success) {
        setStatus('success');
        setMessage(result.message || 'Your password has been reset.');
        return;
      }
      applyFailure(result, 400);
    } catch (error) {
      const response = (error as {
        response?: { status?: number; data?: Partial<PasswordResetResult> };
      }).response;
      applyFailure(response?.data, response?.status);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4 py-12 sm:px-6 lg:px-8">
      {/* Brand-tinted ambient glow (subtle in both themes) */}
      <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-primary/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 h-80 w-80 rounded-full bg-primary/10 blur-3xl" />

      <div className="absolute right-4 top-4">
        <ThemeToggle />
      </div>

      <div className="relative w-full max-w-md animate-fade-in">
        <div className="flex flex-col items-center">
          <Wordmark className="scale-110" />
          <h1 className="mt-6 text-2xl font-bold tracking-tight text-foreground">
            Choose a new password
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Feed aggregation, classification &amp; task tracking
          </p>
        </div>

        <div className="card mt-8 p-8 shadow-modal">
          {status === 'form' && (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <p className="text-sm text-muted-foreground">
                Resetting your password signs you out on every device.
              </p>

              <div className="space-y-4">
                <div>
                  <label htmlFor="password" className="label">
                    New password
                  </label>
                  <div className="relative mt-1.5">
                    <LockIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                      {...register('password')}
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      className="input pl-9 pr-10"
                      placeholder="New password"
                    />
                    <button
                      type="button"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground transition-colors hover:text-foreground"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOffIcon className="h-4 w-4" />
                      ) : (
                        <EyeIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-danger">{errors.password.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="label">
                    Confirm new password
                  </label>
                  <div className="relative mt-1.5">
                    <LockIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                      {...register('confirmPassword')}
                      id="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      className="input pl-9"
                      placeholder="Confirm new password"
                    />
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-danger">{errors.confirmPassword.message}</p>
                  )}
                </div>
              </div>

              {(message || serverErrors.length > 0) && (
                <div role="alert" className="rounded-md border border-danger/30 bg-danger-subtle p-4">
                  {message && <p className="text-sm text-danger-foreground">{message}</p>}
                  {serverErrors.length > 0 && (
                    <ul className="mt-1 list-disc pl-5 text-sm text-danger-foreground">
                      {serverErrors.map((err) => (
                        <li key={err}>{err}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <button type="submit" disabled={isSubmitting} className="btn-primary w-full">
                {isSubmitting && <Spinner className="text-primary-foreground" />}
                {isSubmitting ? 'Saving…' : 'Reset password'}
              </button>
            </form>
          )}

          {status === 'success' && (
            <div className="flex flex-col items-center text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success-subtle">
                <CheckCircle2Icon className="h-7 w-7 text-success" />
              </div>
              <h2 className="mt-4 text-lg font-semibold text-foreground">Password reset</h2>
              <p className="mt-2 text-sm text-muted-foreground">{message}</p>
              <Link to="/login" className="btn-primary mt-6 w-full">
                Continue to sign in
              </Link>
            </div>
          )}

          {status === 'invalid' && (
            <div className="flex flex-col items-center text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-danger-subtle">
                <XCircleIcon className="h-7 w-7 text-danger" />
              </div>
              <h2 className="mt-4 text-lg font-semibold text-foreground">Link not valid</h2>
              <p className="mt-2 text-sm text-muted-foreground">{message}</p>
              <Link to="/forgot-password" className="btn-primary mt-6 w-full">
                Request a new link
              </Link>
              <Link
                to="/login"
                className="mt-4 text-sm font-medium text-primary hover:text-primary/80"
              >
                Back to sign in
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
